import React, { useState, useEffect } from 'react';
import { Typography, Alert } from '@one-thd/sui-atomic-components';
import { isScheduledPickupEnabled } from '@src/constants/fs-values';
import { FULFILLMENT_TYPE } from '@src/constants/status';
import {
  getFulfillmentType,
  getLorpName,
  getLorpPhoneNumber,
  getLorpShipmentsByFulfillmentType,
} from '@src/utils/lorpContactDetailsUtil';
import PostSelectionItems from '@src/components/shared/items/PostSelectionItems';
import { formatPhoneNumber } from '@src/utils/general';
import { ItemToReturn } from '@src/models/returns';
import { ContactDetailsDrawer } from './ContactDetailsDrawer';
import { PhoneNumberDrawer } from '../PhoneNumberDrawer';
import {
  setFetchScheduledPickups,
  setUpdatedShipmentFields,
  useReturnStore,
} from '../../../stores/returnStore';
import { setSelectedPickupDate } from '../../../stores/summaryStore';
import { IDeliveryAddress } from '../../../models/common';
import LORPContactDetails from './LORPContactDetails';
import { LORPTextOptIn } from './LORPTextOptIn';

export interface ILORPContactDetails {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  address?: IDeliveryAddress['postalDetails'];
}

interface LORPContactDetailsStepProps {
  itemsToReturn: ItemToReturn[];
}

const LORPContactDetailsStep = ({
  itemsToReturn,
}: LORPContactDetailsStepProps) => {
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const returnShipments = useReturnStore((state) => state.returnShipments);
  const { deliveryDictionary } = orderDetailsV2 || {};
  const lorpShipments = returnShipments?.filter(
    (shipments) => shipments.shipmentType === 'PICKUP',
  );
  const lorpShipmentIds =
    lorpShipments?.map((shipment) => shipment.shipmentId) || [];
  const [displayDrawer, setDisplayDrawer] = useState(false);
  const [displayPhoneDrawer, setDisplayPhoneDrawer] = useState(false);
  const lorpFulfillmentType = getFulfillmentType(
    lorpShipments,
    deliveryDictionary,
  );
  const displayedContactDetails = lorpShipments?.[0]?.lorpContactDetails;
  const { sthReturnShipments, bossReturnShipments } =
    getLorpShipmentsByFulfillmentType(lorpShipments, deliveryDictionary);
  const updateContactDetails = (updatedContactDetails: ILORPContactDetails) =>
    setUpdatedShipmentFields(lorpShipmentIds, {
      lorpContactDetails: updatedContactDetails,
    });

  useEffect(() => {
    // Will fetch scheduled pickups for LORP and reset any previously selected pickup date
    setFetchScheduledPickups(true);
    setSelectedPickupDate(undefined);
    // Only initialize contact details if they don't already exist (to preserve user input)
    if (displayedContactDetails) {
      return;
    }
    // Getting lineId from appropriate fulfillment type that will be used to get prepopulated info
    // STH and Combination orders will use STH fulfillment group info
    const targetLineId =
      lorpFulfillmentType === FULFILLMENT_TYPE.BOSS
        ? bossReturnShipments?.[0]?.lineItems?.[0].lineItemId
        : sthReturnShipments?.[0]?.lineItems?.[0].lineItemId;
    const shippingInfoFulfillmentGroup = orderDetailsV2?.fulfillmentGroups.find(
      (fulfillmentGroup) =>
        fulfillmentGroup.lineItems?.find(
          (lineItem) => lineItem.lineId === targetLineId,
        ),
    );
    const lorpItemShippingDetails =
      shippingInfoFulfillmentGroup?.shippingAndHandling?.deliveryAddress;
    const { firstName, lastName } = getLorpName(
      shippingInfoFulfillmentGroup,
      lorpFulfillmentType,
    );
    const { postalDetails } = lorpItemShippingDetails || {};
    const phoneNumber = getLorpPhoneNumber(
      shippingInfoFulfillmentGroup,
      lorpFulfillmentType,
    );
    const email = orderDetailsV2?.emailId;
    // Address fields will not be pre-filled(besides state/country) if fulfillment type is BOSS since no address is available from pickup person
    const prefilledAddress =
      lorpFulfillmentType === FULFILLMENT_TYPE.BOSS
        ? {
            state: postalDetails?.state,
            country: postalDetails?.country,
          }
        : postalDetails;
    updateContactDetails({
      firstName,
      lastName,
      email,
      phoneNumber,
      address: prefilledAddress,
    });
  }, []);

  const handlePhoneSave = (phoneNumber: string) =>
    updateContactDetails({
      ...displayedContactDetails,
      phoneNumber,
    });

  return (
    <div>
      <PostSelectionItems selectedReturnItems={itemsToReturn} />
      <div className="sui-mt-3">
        {[FULFILLMENT_TYPE.STH, 'COMBINATION'].includes(
          lorpFulfillmentType,
        ) && (
          <div className="sui-mb-6">
            <Alert status="info" size="large">
              <Typography variant="body-sm">
                If you&apos;d like to change your return pickup address, please
                call 1-800-430-3376
              </Typography>
            </Alert>
          </div>
        )}
        <LORPContactDetails
          contactDetails={displayedContactDetails}
          fulfillmentType={lorpFulfillmentType}
          setDisplayPhoneDrawer={setDisplayPhoneDrawer}
          setDisplayDrawer={setDisplayDrawer}
        />
        {isScheduledPickupEnabled() && <LORPTextOptIn />}
      </div>

      <ContactDetailsDrawer
        displayDrawer={displayDrawer}
        closeDrawer={() => setDisplayDrawer(false)}
        contactDetails={displayedContactDetails}
        setContactDetails={updateContactDetails}
        isSTH={lorpFulfillmentType === FULFILLMENT_TYPE.STH}
      />

      <PhoneNumberDrawer
        open={displayPhoneDrawer}
        onClose={() => setDisplayPhoneDrawer(false)}
        onSave={handlePhoneSave}
        initialPhoneNumber={formatPhoneNumber(
          displayedContactDetails?.phoneNumber,
        )}
      />
    </div>
  );
};

export default LORPContactDetailsStep;
